"use client";

import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card_v3";
import { Badge } from "components/ui/badge_v4";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table_v2";
import { cn } from "lib/utils";
import localization from "services/localization";

const ROLE_COLORS = {
  teacher: "bg-blue-500/15 text-blue-600 border-blue-200",
  student: "bg-emerald-500/15 text-emerald-600 border-emerald-200",
  administrative: "bg-amber-500/15 text-amber-600 border-amber-200",
};

const DARK_ROLE_COLORS = {
  teacher: "bg-blue-500/20 text-blue-400 border-blue-800",
  student: "bg-emerald-500/20 text-emerald-400 border-emerald-800",
  administrative: "bg-amber-500/20 text-amber-400 border-amber-800",
};

function RatingStars({ rating }) {
  const full = Math.floor(rating || 0);
  const fraction = (rating || 0) - full;

  return (
    <div className="flex items-center gap-1">
      <span className="text-sm font-medium text-foreground">
        {(rating || 0).toFixed(1)}
      </span>
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            className={cn(
              "h-3.5 w-3.5",
              i < full
                ? "text-amber-400"
                : i === full && fraction > 0
                ? "text-amber-400/50"
                : "text-muted-foreground/25"
            )}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    </div>
  );
}

export default function UserLeaderboard({
  messages = [],
  users = [],
  departments = [],
  theme,
  selectedDepartment = "all",
}) {
  const [expandedUsers, setExpandedUsers] = useState({});
  const isSingleDepartment =
    selectedDepartment && selectedDepartment !== "all";

  const card_bg =
    theme === "dark"
      ? "bg-[var(--card-bg)] border-border"
      : "bg-card border-border";

  const roleColors = theme === "dark" ? DARK_ROLE_COLORS : ROLE_COLORS;

  const usersMap = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [users]);

  const departmentsMap = useMemo(() => {
    const map = {};
    departments.forEach((d) => {
      map[d.id] = d;
    });
    return map;
  }, [departments]);

  const leaderboard = useMemo(() => {
    const userStats = {};

    messages.forEach((m) => {
      const uid = m.user_id;
      const deptId = m.department_id;
      const totalMessages = m.metrics?.total_messages || 0;
      const avgRating = m.metrics?.avg_rating || 0;
      const avgPolicyRisk = m.metrics?.avg_policy_risk || 0;

      if (!userStats[uid]) {
        const user = usersMap[uid] || {};
        userStats[uid] = {
          userId: uid,
          name: user.name || "Unknown",
          role: user.role || "unknown",
          totalMessages: 0,
          weightedRatingSum: 0,
          weightedRiskSum: 0,
          departments: {},
        };
      }

      userStats[uid].totalMessages += totalMessages;
      userStats[uid].weightedRatingSum += avgRating * totalMessages;
      userStats[uid].weightedRiskSum += avgPolicyRisk * totalMessages;

      if (!userStats[uid].departments[deptId]) {
        userStats[uid].departments[deptId] = {
          departmentId: deptId,
          departmentName: departmentsMap[deptId]?.name || "Unknown",
          totalMessages: 0,
          weightedRatingSum: 0,
          weightedRiskSum: 0,
        };
      }

      userStats[uid].departments[deptId].totalMessages += totalMessages;
      userStats[uid].departments[deptId].weightedRatingSum += avgRating * totalMessages;
      userStats[uid].departments[deptId].weightedRiskSum += avgPolicyRisk * totalMessages;
    });

    return Object.values(userStats)
      .map((user) => {
        const avgRating =
          user.totalMessages > 0
            ? user.weightedRatingSum / user.totalMessages
            : 0;

        const avgPolicyRisk =
          user.totalMessages > 0
            ? user.weightedRiskSum / user.totalMessages
            : 0;

        const departments = Object.values(user.departments)
          .map((dept) => ({
            ...dept,
            avgRating:
              dept.totalMessages > 0
                ? dept.weightedRatingSum / dept.totalMessages
                : 0,
            avgPolicyRisk:
              dept.totalMessages > 0
                ? dept.weightedRiskSum / dept.totalMessages
                : 0,
          }))
          .sort((a, b) => b.totalMessages - a.totalMessages);

        return {
          ...user,
          avgRating,
          avgPolicyRisk,
          departments,
        };
      })
      .sort((a, b) => b.totalMessages - a.totalMessages);
  }, [messages, usersMap, departmentsMap]);

  const toggleExpanded = (userId) => {
    setExpandedUsers((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  if (!leaderboard.length) {
    return (
      <Card className={cn("col-span-full", card_bg)}>
        <CardContent className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
          {localization.no_data || "No data"}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("col-span-full", card_bg)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-foreground">
           {isSingleDepartment
            ? localization.department_users || "Department Users"
            : localization.top_users || "Users Overview"}
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-2">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
               {!isSingleDepartment && <TableHead className="w-[40px]" />}
              <TableHead className="text-xs">#</TableHead>
              <TableHead className="text-xs">{localization.name || "Name"}</TableHead>
              <TableHead className="text-xs">{localization.role || "Role"}</TableHead>
               {isSingleDepartment && (
                <TableHead className="text-xs">
                  {localization.department || "Department"}
                </TableHead>
              )}
              <TableHead className="text-xs text-right">{localization.messages || "Messages"}</TableHead>
              <TableHead className="text-xs text-right">
                {localization.avg_policy_risk || "Policy Risk"}
              </TableHead>
              <TableHead className="text-xs">{localization.rating || "Rating"}</TableHead>

            </TableRow>
          </TableHeader>

          <TableBody>
            {leaderboard.map((user, index) => {
              const selectedDeptData = isSingleDepartment
                ? user.departments.find((d) => d.departmentId === selectedDepartment)
                : null;
              return (
                <>
                <TableRow key={user.userId} className="hover:bg-muted/30">
                  {!isSingleDepartment && (
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => toggleExpanded(user.userId)}
                          className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
                        >
                          {expandedUsers[user.userId] ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </TableCell>
                    )}

                  <TableCell className="font-medium text-muted-foreground text-sm">
                    {index + 1}
                  </TableCell>

                  <TableCell className="font-medium text-foreground text-sm">
                    <div className="flex flex-col">
                      <span>{user.name}</span>
                      {usersMap[user.userId]?.email && (
                        <span className="text-xs text-muted-foreground">
                          {usersMap[user.userId].email}
                        </span>
                      )}
                    </div>
                  </TableCell>

                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-xs capitalize",
                        roleColors[user.role] || ""
                      )}
                    >
                      {user.role}
                    </Badge>
                  </TableCell>

  {isSingleDepartment && (
                      <TableCell className="text-sm text-foreground">
                        {selectedDeptData?.departmentName ||
                          departmentsMap[selectedDepartment]?.name ||
                          "Unknown"}
                      </TableCell>
                    )}


                   <TableCell className="text-right font-medium text-sm text-foreground">
                      {isSingleDepartment
                        ? selectedDeptData?.totalMessages || 0
                        : user.totalMessages}
                    </TableCell>

                  <TableCell className="text-right text-sm text-muted-foreground">
                      {isSingleDepartment
                        ? `${(selectedDeptData?.avgPolicyRisk || 0).toFixed(1)} / 5`
                        : `${user.avgPolicyRisk.toFixed(1)} / 5`}
                    </TableCell>

                 <TableCell>
                      <RatingStars
                        rating={
                          isSingleDepartment
                            ? selectedDeptData?.avgRating || 0
                            : user.avgRating
                        }
                      />
                    </TableCell>
                </TableRow>

                {!isSingleDepartment && expandedUsers[user.userId] && (
                  <TableRow>
                    <TableCell colSpan={7} className="bg-muted/20">
                      <div className="rounded-md border border-border overflow-hidden">
                        <Table>
                          <TableHeader className="bg-muted/50 border-b border-border">
                            <TableRow className="hover:bg-transparent">
                              <TableHead className="text-xs">
                                {localization.department || "Department"}
                              </TableHead>
                              <TableHead className="text-xs text-right">
                                {localization.messages || "Messages"}
                              </TableHead>
                              <TableHead className="text-xs text-right">
                                {localization.avg_policy_risk || "Policy Risk"}
                              </TableHead>
                              <TableHead className="text-xs">
                                {localization.rating || "Rating"}
                              </TableHead>

                            </TableRow>
                          </TableHeader>

                          <TableBody>
                            {user.departments.map((dept) => (
                              <TableRow key={`${user.userId}-${dept.departmentId}`}>
                                <TableCell className="text-sm text-foreground">
                                  {dept.departmentName}
                                </TableCell>
                                <TableCell className="text-right text-sm font-medium text-foreground">
                                  {dept.totalMessages}
                                </TableCell>
                                <TableCell className="text-right text-sm text-muted-foreground">
                                  {dept.avgPolicyRisk.toFixed(1)} / 5
                                </TableCell>
                                <TableCell>
                                  <RatingStars rating={dept.avgRating} />
                                </TableCell>

                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )})}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}